// PDF version of the multi-PDP synthesis report (report_type category |
// brand | comparison) — the "main report" inside the download ZIP and the
// email attachment. Sections mirror MultiAuditSynthesisReport top-to-bottom;
// visual language mirrors PdpAuditPdf (black background, gold accents,
// Oswald display + Lato body) so the PDFs in one ZIP read as one family.
//
// Single wrapping LETTER page: PageHeader/FooterBar are `fixed`, so react-pdf
// repeats them automatically on every overflow page.

import React from "react";
import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import {
    Bullet,
    FooterBar,
    FONT_LATO,
    FONT_LATO_BOLD,
    FONT_OSWALD,
    FONT_OSWALD_BOLD,
    FONT_OSWALD_MEDIUM,
    PageHeader,
} from "./PdpAuditPdf";
import { PdpAudit } from "./types";
import {
    RmnAuditSynthesis,
    RmnBrandReportParams,
    RmnCategoryReportParams,
    RmnComparisonReportParams,
    RmnMultiAuditItem,
    RmnMultiAuditReport,
} from "../../types/rmn";
import { registerPdfFonts } from "../../utils";

registerPdfFonts();

// Palette copied from PdpAuditPdf (kept local on purpose — see its note about
// precomposed opaque gold tones; rgba() strings render wrong in react-pdf).
const GOLD = "#b8966a";
const GOLD_LIGHT = "#d4b896";
const GOLD_BORDER_STRONG = "#5c4a32";
const GOLD_BORDER_HAIR = "#1c1610";
const ROW_ALT_BG = "#0d0a07";
const PANEL_BG = "#13100b";
const BLACK = "#000000";
const WHITE = "#FFFFFF";
const GRAY = "#999999";
const GRAY_LIGHT = "#cccccc";
const GREEN = "#6fbf85";
const ERROR = "#e08585";

const styles = StyleSheet.create({
    page: {
        flexDirection: "column",
        backgroundColor: BLACK,
        color: WHITE,
        paddingTop: 36,
        paddingHorizontal: 44,
        paddingBottom: 70,
        fontFamily: FONT_LATO,
        fontSize: 10,
    },
    h1Wrap: {
        flexDirection: "row",
        alignItems: "flex-end",
        marginBottom: 10,
    },
    h1Display: {
        fontFamily: FONT_OSWALD_BOLD,
        fontSize: 34,
        color: WHITE,
        letterSpacing: 4,
        textTransform: "uppercase",
        lineHeight: 1,
    },
    h1Accent: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 32,
        color: GOLD,
        marginLeft: 8,
        textTransform: "uppercase",
        lineHeight: 1,
    },
    headerPanel: {
        backgroundColor: PANEL_BG,
        borderWidth: 1,
        borderColor: GOLD_BORDER_HAIR,
        padding: 20,
        marginBottom: 28,
    },
    synthesisTitle: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 16,
        color: GOLD,
        marginBottom: 4,
    },
    subtitle: {
        fontFamily: FONT_LATO,
        fontSize: 9,
        color: GRAY,
        letterSpacing: 1,
        marginBottom: 12,
        textTransform: "uppercase",
    },
    summary: {
        fontFamily: FONT_LATO,
        fontSize: 10.5,
        color: GRAY_LIGHT,
        lineHeight: 1.7,
    },
    noSynthesis: {
        fontFamily: FONT_LATO,
        fontSize: 10.5,
        color: ERROR,
        lineHeight: 1.6,
    },
    section: {
        marginBottom: 28,
    },
    sectionH2: {
        fontFamily: FONT_OSWALD_BOLD,
        fontSize: 12,
        color: WHITE,
        letterSpacing: 5,
        textTransform: "uppercase",
        marginBottom: 18,
    },
    rankCard: {
        borderWidth: 1,
        borderColor: GOLD_BORDER_STRONG,
        padding: 16,
        marginBottom: 12,
    },
    rankTopRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        marginBottom: 4,
    },
    rankBadge: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: PANEL_BG,
        borderWidth: 1,
        borderColor: GOLD_BORDER_STRONG,
        alignItems: "center",
        justifyContent: "center",
    },
    rankBadgeFirst: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: GOLD,
        alignItems: "center",
        justifyContent: "center",
    },
    rankBadgeText: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 12,
        color: GOLD,
    },
    rankBadgeTextFirst: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 12,
        color: BLACK,
    },
    rankName: {
        flex: 1,
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 13,
        color: WHITE,
    },
    rankOverall: {
        fontFamily: FONT_OSWALD_BOLD,
        fontSize: 14,
        color: GOLD,
    },
    rankOverallLabel: {
        fontFamily: FONT_LATO,
        fontSize: 7,
        color: GRAY,
        letterSpacing: 1.5,
        textTransform: "uppercase",
    },
    rankUrl: {
        fontFamily: FONT_LATO,
        fontSize: 8,
        color: GRAY,
        marginBottom: 10,
    },
    rankColsRow: {
        flexDirection: "row",
        gap: 20,
    },
    rankCol: {
        flex: 1,
    },
    rankColTitle: {
        fontFamily: FONT_LATO_BOLD,
        fontSize: 8,
        letterSpacing: 1.5,
        textTransform: "uppercase",
        marginBottom: 6,
    },
    table: {
        marginTop: 6,
    },
    tableHeaderRow: {
        flexDirection: "row",
        backgroundColor: GOLD,
    },
    tableHeaderCell: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 10,
        color: BLACK,
        letterSpacing: 2,
        paddingVertical: 10,
        paddingHorizontal: 12,
        textTransform: "uppercase",
    },
    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: GOLD_BORDER_HAIR,
    },
    tableRowAlt: {
        flexDirection: "row",
        backgroundColor: ROW_ALT_BG,
        borderBottomWidth: 1,
        borderBottomColor: GOLD_BORDER_HAIR,
    },
    cellPlacement: {
        fontFamily: FONT_OSWALD,
        fontSize: 10,
        color: GOLD_LIGHT,
        paddingVertical: 11,
        paddingHorizontal: 12,
        lineHeight: 1.45,
    },
    cellBody: {
        fontFamily: FONT_LATO,
        fontSize: 10,
        color: GRAY_LIGHT,
        paddingVertical: 11,
        paddingHorizontal: 12,
        lineHeight: 1.55,
    },
    colPlacement: { width: "25%" },
    colChannel: { width: "20%" },
    colWhy: { width: "55%" },
    numberedRow: {
        flexDirection: "row",
        marginBottom: 8,
    },
    numberedIndex: {
        width: 18,
        color: GOLD,
        fontFamily: FONT_OSWALD,
        fontSize: 10,
    },
    numberedText: {
        flex: 1,
        fontFamily: FONT_LATO,
        fontSize: 10,
        color: GRAY_LIGHT,
        lineHeight: 1.7,
    },
    indexRow: {
        borderTopWidth: 1,
        borderTopColor: GOLD_BORDER_HAIR,
        paddingVertical: 9,
    },
    // Name + score on one line; url/error stack below as siblings. (Texts
    // nested in a flex:1 column inside a centered row overlap in react-pdf.)
    indexTopRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
    },
    indexName: {
        flex: 1,
        fontFamily: FONT_LATO,
        fontSize: 10,
        color: WHITE,
    },
    indexUrl: {
        fontFamily: FONT_LATO,
        fontSize: 7.5,
        color: GRAY,
        marginTop: 2,
    },
    indexError: {
        fontFamily: FONT_LATO,
        fontSize: 8.5,
        color: ERROR,
        marginTop: 2,
    },
    indexScore: {
        fontFamily: FONT_OSWALD_MEDIUM,
        fontSize: 12,
        color: GOLD,
    },
});

interface Props {
    reportType: string; // 'category' | 'brand' | 'comparison'
    report: RmnMultiAuditReport<PdpAudit>;
    params?: RmnCategoryReportParams | RmnBrandReportParams | RmnComparisonReportParams | null;
}

// Two-tone H1 words per report type, matching PdpAuditPdf's "Product Page /
// Audit" treatment.
const H1_WORDS: Record<string, [string, string]> = {
    category: ["Category", "Report"],
    brand: ["Brand", "Report"],
    comparison: ["PDP", "Comparison"],
};

export function MultiAuditSynthesisPdf({ reportType, report, params }: Props) {
    const synthesis: RmnAuditSynthesis | undefined = report?.synthesis;
    const items: RmnMultiAuditItem<PdpAudit>[] = report?.items || [];

    const isCategory = reportType === "category";
    const isBrand = reportType === "brand";
    let overline = "PDP Comparison";
    let rollupName: string | undefined;
    if (isCategory) {
        overline = "Category Report";
        rollupName = (params as RmnCategoryReportParams | null | undefined)?.category_name;
    } else if (isBrand) {
        overline = "Brand Report";
        rollupName = (params as RmnBrandReportParams | null | undefined)?.brand_name;
    }
    const auditedCount = items.filter((i) => i.audit).length;
    const subtitle = isCategory || isBrand
        ? [rollupName, `${auditedCount} product pages audited`].filter(Boolean).join(" · ")
        : `${auditedCount} product pages compared`;

    const [h1Main, h1Accent] = H1_WORDS[reportType] || H1_WORDS.comparison;
    const rankings = synthesis?.rankings || [];
    const commonGaps = synthesis?.commonGaps || [];
    const placements = synthesis?.placementRecommendations || [];
    const recommendations = synthesis?.recommendations || [];

    return (
        <Document>
            <Page size="LETTER" style={styles.page} wrap>
                <PageHeader />

                <View style={styles.h1Wrap}>
                    <Text style={styles.h1Display}>{h1Main}</Text>
                    <Text style={styles.h1Accent}>{h1Accent}</Text>
                </View>

                <View style={styles.headerPanel}>
                    <Text style={styles.synthesisTitle}>
                        {synthesis?.title || overline}
                    </Text>
                    <Text style={styles.subtitle}>{subtitle}</Text>
                    {synthesis?.summary ? (
                        <Text style={styles.summary}>{synthesis.summary}</Text>
                    ) : null}
                    {!synthesis && (
                        <Text style={styles.noSynthesis}>
                            The rollup synthesis is unavailable for this report;
                            the individual page audits are listed below.
                        </Text>
                    )}
                </View>

                {rankings.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionH2}>Rankings</Text>
                        {rankings.map((r) => (
                            <View
                                key={`${r.rank}-${r.url}`}
                                style={styles.rankCard}
                                wrap={false}
                            >
                                <View style={styles.rankTopRow}>
                                    <View
                                        style={
                                            r.rank === 1
                                                ? styles.rankBadgeFirst
                                                : styles.rankBadge
                                        }
                                    >
                                        <Text
                                            style={
                                                r.rank === 1
                                                    ? styles.rankBadgeTextFirst
                                                    : styles.rankBadgeText
                                            }
                                        >
                                            {r.rank}
                                        </Text>
                                    </View>
                                    <Text style={styles.rankName}>
                                        {r.productName || "Unknown product"}
                                    </Text>
                                    <Text style={styles.rankOverall}>
                                        {r.overall}%{" "}
                                        <Text style={styles.rankOverallLabel}>
                                            Overall
                                        </Text>
                                    </Text>
                                </View>
                                <Text style={styles.rankUrl}>{r.url}</Text>
                                <View style={styles.rankColsRow}>
                                    {(r.highlights || []).length > 0 && (
                                        <View style={styles.rankCol}>
                                            <Text
                                                style={[
                                                    styles.rankColTitle,
                                                    { color: GREEN },
                                                ]}
                                            >
                                                Highlights
                                            </Text>
                                            {r.highlights.map((text, i) => (
                                                <Bullet key={i} text={text} />
                                            ))}
                                        </View>
                                    )}
                                    {(r.gaps || []).length > 0 && (
                                        <View style={styles.rankCol}>
                                            <Text
                                                style={[
                                                    styles.rankColTitle,
                                                    { color: GRAY_LIGHT },
                                                ]}
                                            >
                                                Gaps
                                            </Text>
                                            {r.gaps.map((text, i) => (
                                                <Bullet key={i} text={text} />
                                            ))}
                                        </View>
                                    )}
                                </View>
                            </View>
                        ))}
                    </View>
                )}

                {commonGaps.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionH2}>Common Gaps</Text>
                        {commonGaps.map((text, i) => (
                            <Bullet key={i} text={text} />
                        ))}
                    </View>
                )}

                {placements.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionH2}>
                            In-Network Placement Recommendations
                        </Text>
                        <View style={styles.table}>
                            <View style={styles.tableHeaderRow} fixed>
                                <Text style={[styles.tableHeaderCell, styles.colPlacement]}>
                                    Placement
                                </Text>
                                <Text style={[styles.tableHeaderCell, styles.colChannel]}>
                                    Channel
                                </Text>
                                <Text style={[styles.tableHeaderCell, styles.colWhy]}>
                                    Why
                                </Text>
                            </View>
                            {placements.map((p, i) => (
                                <View
                                    key={i}
                                    style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
                                    wrap={false}
                                >
                                    <Text style={[styles.cellPlacement, styles.colPlacement]}>
                                        {p.placement}
                                    </Text>
                                    <Text style={[styles.cellBody, styles.colChannel]}>
                                        {p.channel}
                                    </Text>
                                    <Text style={[styles.cellBody, styles.colWhy]}>
                                        {p.reason}
                                    </Text>
                                </View>
                            ))}
                        </View>
                    </View>
                )}

                {recommendations.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionH2}>Recommended Next Steps</Text>
                        {recommendations.map((text, i) => (
                            <View key={i} style={styles.numberedRow} wrap={false}>
                                <Text style={styles.numberedIndex}>{i + 1}.</Text>
                                <Text style={styles.numberedText}>{text}</Text>
                            </View>
                        ))}
                    </View>
                )}

                {items.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionH2}>Individual Page Audits</Text>
                        {items.map((item, idx) => (
                            <View key={`${idx}-${item.url}`} style={styles.indexRow} wrap={false}>
                                <View style={styles.indexTopRow}>
                                    <Text style={styles.indexName}>
                                        {item.audit?.productName || item.url}
                                    </Text>
                                    {typeof item.audit?.scores?.overall === "number" && (
                                        <Text style={styles.indexScore}>
                                            {item.audit.scores.overall}%
                                        </Text>
                                    )}
                                </View>
                                {item.audit?.productName ? (
                                    <Text style={styles.indexUrl}>{item.url}</Text>
                                ) : null}
                                {item.error ? (
                                    <Text style={styles.indexError}>
                                        Audit failed: {item.error}
                                    </Text>
                                ) : null}
                            </View>
                        ))}
                    </View>
                )}

                <FooterBar />
            </Page>
        </Document>
    );
}
